import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  BookOpen, ArrowLeft, Plus, Trash2, Loader, Calendar,
  MessageSquare, Quote, Music, Heart, HandHeart, ChevronDown, ChevronUp
} from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

export default function ChurchStudyPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadStudies();
  }, []);

  const loadStudies = async () => {
    try {
      const { data } = await axios.get(`${API}/church/study`);
      setStudies(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (studyId) => {
    if (!window.confirm(t('confirm_delete_study'))) return;
    
    try {
      await axios.delete(`${API}/church/study/${studyId}`);
      setStudies(studies.filter(s => s.id !== studyId));
    } catch (e) {
      alert(e.response?.data?.detail || t('could_not_delete'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#9B5DE5" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)" }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/church")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4">
            <ArrowLeft size={16} /> {t('back')}
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-2xl p-3">
                <BookOpen size={28} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('church_bible_study')}
                </h1>
                <p className="text-white/70 text-sm font-medium">{t('create_topics_for_families')}</p>
              </div>
            </div>
            <button onClick={() => setShowCreateModal(true)}
              className="bg-white text-[#9B5DE5] font-black px-4 py-2 rounded-xl flex items-center gap-1"
              data-testid="create-study-btn">
              <Plus size={18} /> {t('create')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-5">
        {studies.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <BookOpen size={48} className="mx-auto mb-3 opacity-40" color="#9B5DE5" />
            <p className="text-gray-500 font-medium">{t('no_studies_yet')}</p>
            <p className="text-gray-400 text-sm">{t('create_first_study')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {studies.map((study) => (
              <StudyCard key={study.id} study={study} onDelete={handleDelete} t={t} />
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateStudyModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={() => { setShowCreateModal(false); loadStudies(); }}
          t={t}
        />
      )}
    </div>
  );
}

// Study Card with all worship sections
function StudyCard({ study, onDelete, t }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-black text-lg text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {study.title}
            </p>
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
              <Calendar size={12} /> {new Date(study.created_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={() => onDelete(study.id)}
            className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"
            data-testid={`delete-study-${study.id}`}>
            <Trash2 size={18} />
          </button>
        </div>
        <button onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 text-[#9B5DE5] text-sm font-bold mt-2 py-2 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          {expanded ? "Show Less" : "View Full Study Guide"}
        </button>
      </div>

      {expanded && (
        <div className="p-5 space-y-4">
          {/* 1. Start with Prayer */}
          <div className="rounded-2xl p-4 border-l-4" style={{ borderColor: "#9B5DE5", background: "#F8F5FF" }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">🙏</span>
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Opening Prayer
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              {study.opening_prayer || "Open with a prayer inviting God's presence. Ask the Holy Spirit to guide your hearts and minds as you study His Word together."}
            </p>
          </div>

          {/* 2. Sing a Few Songs */}
          <div className="rounded-2xl p-4 border-l-4" style={{ borderColor: "#FFD166", background: "#FFFBEB" }}>
            <div className="flex items-center gap-2 mb-2">
              <Music size={20} color="#F77F00" />
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Suggested Songs
              </h4>
            </div>
            {study.suggested_songs && study.suggested_songs.length > 0 ? (
              <ul className="space-y-2">
                {study.suggested_songs.map((song, i) => (
                  <li key={i}>
                    <a 
                      href={`https://www.youtube.com/results?search_query=${encodeURIComponent(song + ' hymn')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-700 flex items-center gap-2 hover:text-[#FF0000] transition-colors group"
                    >
                      <span className="w-6 h-6 rounded flex items-center justify-center bg-[#FF0000] group-hover:scale-110 transition-transform">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="white">
                          <polygon points="9.5,7.5 16.5,12 9.5,16.5" />
                        </svg>
                      </span>
                      <span className="group-hover:underline">{song}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600 leading-relaxed">
                Sing 2-3 worship songs together. Choose songs that relate to today's lesson theme.
              </p>
            )}
          </div>

          {/* 3. Words of Thanksgiving */}
          <div className="rounded-2xl p-4 border-l-4" style={{ borderColor: "#06D6A0", background: "#E6FFF7" }}>
            <div className="flex items-center gap-2 mb-2">
              <Heart size={20} color="#06D6A0" />
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Words of Thanksgiving to God
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Take a moment to share thanksgiving. Invite families to share what they're grateful for this week.
            </p>
          </div>

          {/* 4. Bible Verse */}
          <div className="rounded-2xl p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
            <Quote size={20} color="white" className="mb-2 opacity-70" />
            <p className="text-white font-semibold italic mb-2">"{study.bible_verse}"</p>
            <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">
              {study.verse_reference}
            </span>
          </div>

          {/* 5. Description/Teaching */}
          <div className="rounded-2xl p-4 bg-gray-50 border-l-4" style={{ borderColor: "#118AB2" }}>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen size={20} color="#118AB2" />
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Today's Teaching
              </h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{study.description}</p>
          </div>

          {/* 6. Discussion Questions */}
          <div className="rounded-2xl p-4 bg-purple-50">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={20} color="#9B5DE5" />
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Discussion Questions
              </h4>
            </div>
            <ul className="space-y-2">
              {study.discussion_questions?.map((q, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2 bg-white p-3 rounded-xl">
                  <span className="font-black text-[#9B5DE5] bg-purple-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{q}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 7. Family Activity */}
          {study.family_activity && (
            <div className="rounded-2xl p-4 border-l-4" style={{ borderColor: "#06D6A0", background: "#E0FFF5" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🎮</span>
                <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Family Activity
                </h4>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{study.family_activity}</p>
            </div>
          )}

          {/* 8. Finish with Prayer */}
          <div className="rounded-2xl p-4 border-l-4" style={{ borderColor: "#EF476F", background: "#FFF5F7" }}>
            <div className="flex items-center gap-2 mb-2">
              <HandHeart size={20} color="#EF476F" />
              <h4 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Closing Prayer
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              {study.closing_prayer || "Close with a prayer of blessing. Thank God for the time together and pray for the week ahead."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateStudyModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    bible_verse: "",
    verse_reference: "",
    discussion_questions: ["", "", ""],
    suggested_songs: ["", ""],
    opening_prayer: "",
    closing_prayer: "",
    family_activity: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/church/study`, {
        ...form,
        discussion_questions: form.discussion_questions.filter(q => q.trim()),
        suggested_songs: form.suggested_songs.filter(s => s.trim())
      });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create study");
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = (index, value) => {
    const newQuestions = [...form.discussion_questions];
    newQuestions[index] = value;
    setForm({ ...form, discussion_questions: newQuestions });
  };

  const updateSong = (index, value) => {
    const newSongs = [...form.suggested_songs];
    newSongs[index] = value;
    setForm({ ...form, suggested_songs: newSongs });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 my-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
          Create Bible Study
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none"
              placeholder="e.g., The Parable of the Good Samaritan"
              required
              data-testid="study-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Bible Verse</label>
            <textarea
              value={form.bible_verse}
              onChange={(e) => setForm({ ...form, bible_verse: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none resize-none"
              rows={3}
              placeholder="Enter the Bible verse text..."
              required
              data-testid="study-verse-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Verse Reference</label>
            <input
              type="text"
              value={form.verse_reference}
              onChange={(e) => setForm({ ...form, verse_reference: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none"
              placeholder="e.g., Luke 10:25-37"
              required
              data-testid="study-reference-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Description/Teaching</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none resize-none"
              rows={3}
              placeholder="The main teaching points for families..."
              required
              data-testid="study-description-input"
            />
          </div>

          {/* Suggested Songs */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1 flex items-center gap-2">
              <Music size={16} color="#FFD166" /> Suggested Songs
            </label>
            {form.suggested_songs.map((s, i) => (
              <input
                key={i}
                type="text"
                value={s}
                onChange={(e) => updateSong(i, e.target.value)}
                className="w-full px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-[#FFD166] outline-none mb-2"
                placeholder={`Song ${i + 1} (e.g., Amazing Grace)`}
                data-testid={`study-song-${i}`}
              />
            ))}
          </div>

          {/* Opening Prayer */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1 flex items-center gap-2">
              🙏 Opening Prayer
            </label>
            <textarea
              value={form.opening_prayer}
              onChange={(e) => setForm({ ...form, opening_prayer: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none resize-none"
              rows={2}
              placeholder="A suggested opening prayer for families..."
              data-testid="study-opening-prayer"
            />
          </div>

          {/* Discussion Questions */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Discussion Questions</label>
            {form.discussion_questions.map((q, i) => (
              <input
                key={i}
                type="text"
                value={q}
                onChange={(e) => updateQuestion(i, e.target.value)}
                className="w-full px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] outline-none mb-2"
                placeholder={`Question ${i + 1}...`}
                data-testid={`study-question-${i}`}
              />
            ))}
          </div>

          {/* Family Activity */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1 flex items-center gap-2">
              🎮 Family Activity
            </label>
            <textarea
              value={form.family_activity}
              onChange={(e) => setForm({ ...form, family_activity: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#06D6A0] outline-none resize-none"
              rows={2}
              placeholder="A fun activity or game for families to do together..."
              data-testid="study-family-activity"
            />
          </div>

          {/* Closing Prayer */}
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1 flex items-center gap-2">
              <HandHeart size={16} color="#EF476F" /> Closing Prayer
            </label>
            <textarea
              value={form.closing_prayer}
              onChange={(e) => setForm({ ...form, closing_prayer: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#EF476F] outline-none resize-none"
              rows={2}
              placeholder="A suggested closing prayer or blessing..."
              data-testid="study-closing-prayer"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold border-2 border-gray-200 text-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-3 rounded-xl font-bold text-white disabled:opacity-50"
              style={{ background: "#9B5DE5" }}
              data-testid="submit-create-study">
              {loading ? <Loader size={20} className="mx-auto animate-spin" /> : "Create Study"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
